import { updateBillingInformation } from "@/composables/useBillingInformation";
import { Address } from "@/interfaces/address";
import type { BillingInformation } from "@/interfaces/billingInformations";
import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import type { MutationParams } from "@/interfaces/mutationParams";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { get } from "@vueuse/core";
import { MaybeRef } from "vue";
import { ToastInterface, useToast } from "vue-toastification";

interface Props {
  params: Params;
  immediate?: boolean;
}

interface Params extends Omit<Partial<BillingInformation>, "id"> {
  id: MaybeRef<string | number> | string | number;
}

interface CreateParams {
  file: File;
  id: string | number;
}

const getHotelBillingInformation = async (params: Params) => {
  const { data } = await axios.get<BillingInformation[]>(
    `/api/internal/hotels/${get(params.id) || params.id}/billing-informations`,
    { errorHandling: false } as CustomAxiosRequestConfig,
  );

  return data[0] ?? null;
};

export const createHotelBillingInformation = async (
  params: CreateParams,
  toast: ToastInterface,
) => {
  try {
    const formData = new FormData();
    formData.append("file", params.file);

    const { data } = await axios.post<BillingInformation>(
      `/api/internal/hotels/${params.id}/billing-informations`,
      formData,
    );

    toast.success("La información fiscal se ha creado correctamente");

    return data;
  } catch (e: any) {
    toast.error(e.response?.data.message);
  }
};

const useHotelBillingInformation = (props: Props) => {
  const { params, immediate = true } = props;
  const toast = useToast();

  const { data, isLoading, isError, refetch } = useQuery(
    ["HotelBillingInformation", params.id],
    () => getHotelBillingInformation(params),
    {
      enabled: immediate,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const createMutation = useMutation((props: MutationParams<CreateParams>) =>
    createHotelBillingInformation(props.params, toast),
  );

  return {
    data,
    isError,
    isLoading,
    refetch,

    createHotelBillingInformationMutation: createMutation,
    createHotelBillingInformation: createMutation.mutate,
    createHotelBillingInformationAsync: createMutation.mutateAsync,
    createHotelBillingInformationLoading: createMutation.isLoading,
  };
};

export default useHotelBillingInformation;
